import { Dayjs } from 'dayjs';
import React, { useMemo } from 'react';
import { CartesianGrid, Label, ReferenceArea, ReferenceDot, XAxis, YAxis } from 'recharts';
import { colors, palette } from 'design';

export type FertilityDot = {
  date: Dayjs;
  type: 'fertile' | 'ovulation';
  predicted?: boolean;
};

export type CalendarChartProps = {
  dayLabels: Dayjs[];
  dayRange?: [Dayjs, Dayjs];
  periodDots?: Dayjs[];
  drawHollowPeriodDots?: boolean;
  fertilityDots?: FertilityDot[];
  topLabels?: [Dayjs, string][];
  showMonthLabels?: boolean;
  // shrinks the graph to just fit a single row of dots/icons, for charts that can only ever have one data point per day
  compact?: boolean;
  yRangeOverride?: number;
};

// Fixed pixel height of a single data unit (eg. the bottom day-number row), so that row stays the same size
// across compact and non-compact charts instead of being squashed along with the rest of the graph.
const PIXELS_PER_UNIT = 14;

// Pure version of the graphHeight calculation below, exposed so callers (eg. the history module) can know a
// chart's height upfront, without rendering it, to keep sibling layouts (like a column of titles) in sync.
export function calculateCalendarChartHeight({
  hasBottomDots = false,
  showMonthLabels = false,
  hasTopLabels = false,
  compact = false,
  yRangeOverride,
}: {
  hasBottomDots?: boolean;
  showMonthLabels?: boolean;
  hasTopLabels?: boolean;
  compact?: boolean;
  yRangeOverride?: number;
} = {}): number {
  const yRange = yRangeOverride ?? (compact ? 2 : 5);
  const simpleGraphHeight = yRange * PIXELS_PER_UNIT;
  const topLabelsHeight = hasTopLabels ? 24 : 0;
  return simpleGraphHeight + (hasBottomDots ? 10 : 0) + (showMonthLabels ? 15 : 0) + topLabelsHeight;
}

export default function useCalendarChart({
  dayLabels,
  dayRange,
  periodDots,
  drawHollowPeriodDots = false,
  fertilityDots,
  topLabels,
  showMonthLabels = false,
  compact = false,
  yRangeOverride,
}: CalendarChartProps) {
  // Calculate domain boundaries
  const { displayedMinDay, displayedMaxDay, domainMinDay, domainMaxDay } = useMemo(
    () => ({
      displayedMinDay: dayLabels[0],
      displayedMaxDay: dayLabels[dayLabels.length - 1],
      domainMinDay: dayRange ? dayRange[0] : dayLabels[0],
      domainMaxDay: dayRange ? dayRange[1] : dayLabels[dayLabels.length - 1],
    }),
    [dayLabels, dayRange],
  );

  // Generate days in domain
  const daysInDomain = useMemo(
    () => Array.from({ length: domainMaxDay.diff(domainMinDay, 'days') + 1 }, (_, i) => domainMinDay.add(i, 'days')),
    [domainMinDay, domainMaxDay],
  );

  // Calculate first days of months
  const firstDaysOfMonths = useMemo(() => {
    if (showMonthLabels) {
      return dayLabels.filter(day => day.date() === 1);
    }
    return [];
  }, [dayLabels, showMonthLabels]);

  // Calculate dimensions and ranges
  const dimensions = useMemo(() => {
    const yRange = yRangeOverride ?? (compact ? 2 : 5);
    const simpleGraphHeight = yRange * PIXELS_PER_UNIT;
    const topLabelsHeight = topLabels ? 24 : 0;
    const pixelsPerUnit = PIXELS_PER_UNIT;
    const hasBottomDots = Boolean(periodDots) || Boolean(fertilityDots);
    const graphHeight = calculateCalendarChartHeight({
      hasBottomDots,
      showMonthLabels,
      hasTopLabels: Boolean(topLabels),
      compact,
      yRangeOverride,
    });
    const yAxisDomain = [
      (0 - (hasBottomDots ? 10 : 0) - (showMonthLabels ? 15 : 0)) / pixelsPerUnit,
      topLabels ? yRange + topLabelsHeight / pixelsPerUnit : yRange,
    ];
    const dotsY = hasBottomDots ? -5 / pixelsPerUnit : 0;

    return {
      yRange,
      simpleGraphHeight,
      topLabelsHeight,
      pixelsPerUnit,
      graphHeight,
      yAxisDomain,
      dotsY,
    };
  }, [periodDots, fertilityDots, showMonthLabels, topLabels, compact, yRangeOverride]);

  const verticalGridLines = useMemo(() => {
    return Array.from({ length: daysInDomain.length }, (_, index) => index * 14);
  }, [daysInDomain]);

  // Caret down component
  const caretDown = useMemo(
    () =>
      ({ cx, cy }: { cx: number; cy: number }) => {
        if (cx == null || cy == null || isNaN(cx) || isNaN(cy)) return null;
        return (
          <svg width={4} height={4} style={{ overflow: 'visible' }}>
            <polygon points={`${cx - 5},${cy - 2} ${cx + 5},${cy - 2} ${cx},${cy + 4}`} fill={colors.border.default} />
          </svg>
        );
      },
    [],
  );

  // Generate chart building blocks
  const chartBuildingBlocks = useMemo(
    () => (
      <>
        <XAxis
          type="number"
          dataKey="dayIndex"
          domain={[0, domainMaxDay.diff(domainMinDay, 'days') + 1]}
          hide
          allowDataOverflow
        />
        <YAxis dataKey="dayValue" domain={dimensions.yAxisDomain} hide allowDataOverflow />
        <ReferenceArea
          key={`daybar-background-${domainMinDay.format('YYYY-MM-DD')}`}
          x1={0}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={0}
          y2={1}
          zIndex={-1}
          fill={colors.dataViz.grid}
          fillOpacity={1}
          stroke={colors.surface.page}
          strokeWidth={2}
          ifOverflow="visible"
        />
        <CartesianGrid
          horizontal={false}
          stroke={colors.surface.page}
          strokeWidth={2}
          verticalPoints={verticalGridLines}
          zIndex={0}
        />
        <ReferenceArea
          key={`hide-grid-top-${domainMinDay.format('YYYY-MM-DD')}`}
          x1={0}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={dimensions.yRange}
          y2={dimensions.yRange + 10}
          fill="white"
          fillOpacity={1}
          ifOverflow="visible"
        />
        <ReferenceArea
          key={`hide-grid-bottom-${domainMinDay.format('YYYY-MM-DD')}`}
          x1={0}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={0}
          y2={-10}
          fill="white"
          fillOpacity={1}
          ifOverflow="visible"
        />
        {daysInDomain.map((day, index) =>
          day.isoWeekday() < 6 ? (
            // using simple svg text to minimize react components and speed up rendering
            <text
              key={`daybar-text-${day.format('YYYY-MM-DD')}-${index}`}
              x={index * 14 + 7}
              textAnchor="middle"
              y={dimensions.simpleGraphHeight + dimensions.topLabelsHeight - 4}
              fill={colors.dataViz.axis}
              style={{ fontSize: 8, color: colors.dataViz.axis }}
            >
              {day.date()}
            </text>
          ) : (
            <ReferenceArea
              key={`daybar-${day.format('YYYY-MM-DD')}-${index}`}
              x1={index}
              x2={index + 1}
              y1={0}
              y2={1}
              fill={colors.dataViz.axis}
              fillOpacity={1}
              stroke={colors.surface.page}
              strokeWidth={2}
              ifOverflow="visible"
              label={<Label fontSize={8} value={day.date()} fill="white" />}
            />
          ),
        )}
        {periodDots?.map((day, index) => (
          <ReferenceDot
            key={`perioddot-${day.format('YYYY-MM-DD')}-${index}`}
            x={day.diff(domainMinDay, 'days') + 0.5}
            y={dimensions.dotsY}
            r={3}
            fill={colors.dataViz.veryBad}
            fillOpacity={drawHollowPeriodDots ? 0 : 1}
            stroke={colors.dataViz.veryBad}
            strokeWidth={drawHollowPeriodDots ? 1 : 0}
            ifOverflow="visible"
          />
        ))}
        {fertilityDots?.map(({ date, type, predicted }, index) => {
          const color = type === 'ovulation' ? palette.teal[900] : palette.teal[500];
          return (
            <ReferenceDot
              key={`fertilitydot-${date.format('YYYY-MM-DD')}-${index}`}
              x={date.diff(domainMinDay, 'days') + 0.5}
              y={dimensions.dotsY}
              r={3}
              fill={color}
              fillOpacity={predicted ? 0 : 1}
              stroke={color}
              strokeWidth={predicted ? 1 : 0}
              ifOverflow="visible"
            />
          );
        })}
        {topLabels?.map(([day, label], index) => (
          <ReferenceDot
            key={`toplabel-${day.format('YYYY-MM-DD')}-${index}`}
            x={day.diff(domainMinDay, 'days') + 0.5}
            y={dimensions.yAxisDomain[1] - 1.2}
            r={2}
            fill={colors.text.tertiary}
            fillOpacity={1}
            strokeWidth={0}
            shape={caretDown}
            label={<Label position="top" fontWeight={700} fontSize={13} value={label} fill={colors.text.tertiary} />}
          />
        ))}
        {firstDaysOfMonths.map((day, index) => {
          return (
            <React.Fragment key={`month-${day.format('YYYY-MM-DD')}-${index}`}>
              <ReferenceArea
                x1={day.diff(domainMinDay, 'days')}
                x2={day.diff(domainMinDay, 'days') + 0.1}
                y1={0}
                y2={-2}
                fillOpacity={0}
                strokeWidth={2}
                stroke={colors.surface.page}
                ifOverflow="visible"
              />
              <ReferenceArea
                x1={day.diff(domainMinDay, 'days')}
                x2={day.diff(domainMinDay, 'days')}
                y1={dimensions.yAxisDomain[0] + 0.5}
                y2={dimensions.yAxisDomain[0] + 0.5}
                fillOpacity={0}
                strokeWidth={0}
                label={
                  <Label
                    position="right"
                    fontSize={13}
                    value={day.format('MMMM').charAt(0).toUpperCase() + day.format('MMMM').slice(1).toLowerCase()}
                    fill={colors.text.disabled}
                  />
                }
              />
            </React.Fragment>
          );
        })}
        <ReferenceArea
          key={`gray-overlay-left-${domainMinDay.format('YYYY-MM-DD')}`}
          x1={0}
          x2={displayedMinDay.diff(domainMinDay, 'days')}
          y1={0}
          y2={5}
          fill={colors.surface.page}
          fillOpacity={0.5}
          ifOverflow="visible"
        />
        <ReferenceArea
          key={`gray-overlay-right-${domainMinDay.format('YYYY-MM-DD')}`}
          x1={displayedMaxDay.diff(domainMinDay, 'days') + 1}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={0}
          y2={5}
          fill={colors.surface.page}
          fillOpacity={0.5}
          ifOverflow="visible"
        />
      </>
    ),
    [
      daysInDomain,
      domainMinDay,
      domainMaxDay,
      dimensions.yAxisDomain,
      periodDots,
      fertilityDots,
      topLabels,
      firstDaysOfMonths,
      displayedMinDay,
      displayedMaxDay,
      caretDown,
      verticalGridLines,
      drawHollowPeriodDots,
    ],
  );

  return {
    graphHeight: dimensions.graphHeight,
    yRange: dimensions.yRange,
    simpleGraphHeight: dimensions.simpleGraphHeight,
    topLabelsHeight: dimensions.topLabelsHeight,
    pixelsPerUnit: dimensions.pixelsPerUnit,
    yAxisDomain: dimensions.yAxisDomain,
    domainMaxDay,
    domainMinDay,
    chartBuildingBlocks,
  };
}
